<template>
  <BContainer>
    <BRow align-v="center">
      <BCol lg="6" order-lg="2" class="text-right pl-lg-5">
        <cld-image
          class="w-50 d-block mx-auto"
          public-id="v1557870238/state_of_visual_media/cloudinary-slide6.svg"
          alt="A stopwatch running out of time with a brain and a lightning bolt next to the watch tick"
          fluid
         />
        <h2 class="my-4">First impressions happen <i>before</i> seeing any visuals</h2>
        <p>Images get across the finish line faster when they are fewer bytes. Different browsers and apps can support different advanced image formats—and the right formats could make all the difference when it comes to telling visual stories.</p>
        <!-- <p>Whereas most studies say brands have 7 seconds to make a first impression with their marketing, when it comes to visuals, that drops to 1/2 a second.</p> -->
        <!-- <i>A viewer’s first impression, referred to as “thin slicing,” starts before an image completely renders.</i> -->
        <BRow>
          <BCol class="text-left">
            <v-popover popover-class="pp-100">
              <LearnMore />
              <template slot="popover">
                <div class="popover-title" style="margin: 0;">
                  <cld-image class="tooltip-finger-icon" public-id="v1557870041/state_of_visual_media/cloudinary-finger.svg" alt="Icon of a hand with the index finger pointing up, representing a tool tip" />
                  <strong>Adoption of Webp for Chrome by industry</strong>
                </div>
                <BRow class="ml-3 mt-3">
                  <BCol sm="4" class="text-center border-global">
                    <ChartDonut :dark="true" :num="numbers.webp.global" class="chart-donut mt-3 mb-1 mx-auto" chart-id="ChartDonut-global" />
                    <p class="mt-2">Global</p>
                  </BCol>
                  <BCol sm="8">
                    <BRow>
                      <BCol cols="6" class="text-center">
                        <ChartDonut :dark="true" :num="numbers.webp.automotive" class="chart-donut mt-3 mb-1 mx-auto" chart-id="ChartDonut-automotive" />
                        <p class="mt-2">Automotive</p>
                      </BCol>
                      <BCol cols="6" class="text-center">
                        <ChartDonut :dark="true" :num="numbers.webp.apparel" class="chart-donut mt-3 mb-1 mx-auto" chart-id="ChartDonut-apparel" />
                        <p class="mt-2">Apparel</p>
                      </BCol>
                      <BCol cols="6" class="text-center">
                        <ChartDonut :dark="true" :num="numbers.webp.sports" class="chart-donut mt-3 mb-1 mx-auto" chart-id="ChartDonut-sports" />
                        <p class="mt-2">Sports</p>
                      </BCol>
                      <BCol cols="6" class="text-center">
                        <ChartDonut :dark="true" :num="numbers.webp.media" class="chart-donut mt-3 mb-1 mx-auto" chart-id="ChartDonut-media" />
                        <p class="mt-2">Media</p>
                      </BCol>
                    </BRow>
                  </BCol>
                </BRow>
              </template>
            </v-popover>
          </BCol>
          <BCol>
            <v-popover popover-class="pp-20">
              <LearnMore />
              <template slot="popover">
                <ChartDonut :dark="true" :num="numbers.visual" class="chart-donut ml-4" />
                <div class="popover-title" style="margin: 0;">
                  <cld-image class="tooltip-finger-icon" public-id="v1557870041/state_of_visual_media/cloudinary-finger.svg" alt="Icon of a hand with the index finger pointing up, representing a tool tip" />
                  <p>
                    <strong>{{ numbers.visual }}%</strong> of the information our brains process is visual*
                  </p>
                </div>
                <small class="d-block ml-4 mt-4">*Fast Company</small>
              </template>
            </v-popover>
          </BCol>
        </BRow>
      </BCol>
      <BCol lg="6" class="p-lg-5">
        <!-- <p>The right formats could make all the difference when it comes to telling visual stories.</p> -->
        <strong class="d-block mb-3">Newer file formats are more efficient</strong>
        <!-- <div class="d-flex w-100 h-100" style="background: #ddd;">
          <h3 class="m-auto" style="color: #888;">
            <strong>Placeholder Chart</strong>
          </h3>
        </div>-->
        <!-- <ChartLine :chartdata="chartableBits" /> -->
        <!-- <HorizontalBars :chartdata="chartableBits" /> -->
        <cld-image
          fluid
          public-id="v1557870260/state_of_visual_media/cloudinary-bit_savings.svg"
          alt="A chart showing Webp roughly 18% smaller than JPEG, JPEG2000 16% smaller than JPEG, and HEIF 36% smaller than JPEG"
          />
        <p class="mt-4 text-center"><small>Relative size of new formats compared to traditional JPEG</small></p>
      </BCol>
    </BRow>
    <footer class="w-md-50">
      <p class="small">Sources: Fast Company, Harvard Study of Communications, Carleton University, Human-Oriented Technology Lab</p>
    </footer>
  </BContainer>
</template>

<script>
import LearnMore from "../LearnMore.vue";
import ChartDonut from "../donut-filled.vue";
// import HorizontalBars from "../horizontal-bars.vue";
// import ChartLine from "../line3.vue";

export default {
  components: {
    LearnMore,
    // HorizontalBars,
    ChartDonut
    // ChartLine
  },
  props: {
    numbers: {
      type: Object,
      default: () => {
        return {};
      }
    }
  },
  computed: {
    chartableBits() {
      // console.log(mobile);
      // console.log(this.numbers.bits["Webp"]);
      // console.log(this.numbers.bits["Jpeg 2000"]);
      // console.log(this.numbers.bits["HEIF"]);

      const numWebp = this.numbers.bits["Webp"];
      const numJpeg = this.numbers.bits["Jpeg 2000"];
      const numHEIF = this.numbers.bits["HEIF"];

      const color1 = "#0091ff";
      const color2 = "#f86726";
      const color3 = "#f2d864";

      let transformed = {
        datasets: [
          {
            label: "5th percentile",
            data: numWebp,
            backgroundColor: `rgba(${this.hexToRgb(color1)}, 0.75)`,
            borderColor: color1,
            borderWidth: 2,
            fill: true
          },
          {
            label: "25th percentile",
            data: numJpeg,
            backgroundColor: `rgba(${this.hexToRgb(color2)}, 0.75)`,
            borderColor: color2,
            borderWidth: 2,
            fill: true
          },
          {
            label: "Median",
            data: numHEIF,
            backgroundColor: `rgba(${this.hexToRgb(color3)}, 0.75)`,
            borderColor: color3,
            borderWidth: 2,
            fill: true
          }
        ],
        labels: ["Webp", "Jpeg 2000", "HEIF"]
        // labels: ["5th percentile", "25th percentile", "Median", "75th percentile", "95th percentile"]
      };

      return transformed;
    }
  }
};
</script>

<style lang="scss" scoped>
.border-global {
  border-right: 1px solid white;
  @include media-breakpoint-down(xs) {
    border-right: none;
    border-bottom: 1px solid white;
  }
}
</style>
